import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { beasts, sortBeastsByCR } from '@character-creator/data';
import type { Beast, ChallengeRating } from '@character-creator/types';
import { LinkedText } from '../components/LinkedText';

const CR_OPTIONS: { value: ChallengeRating | 'all'; label: string }[] = [
  { value: 'all', label: 'All CRs' },
  { value: '0', label: 'CR 0' },
  { value: '1/8', label: 'CR 1/8' },
  { value: '1/4', label: 'CR 1/4' },
  { value: '1/2', label: 'CR 1/2' },
  { value: '1', label: 'CR 1' },
  { value: '2', label: 'CR 2' },
];

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatSpeed(speed: Beast['speed']): string {
  const parts: string[] = [];
  if (speed.walk) parts.push(`${speed.walk} ft.`);
  if (speed.fly) parts.push(`fly ${speed.fly} ft.`);
  if (speed.swim) parts.push(`swim ${speed.swim} ft.`);
  if (speed.climb) parts.push(`climb ${speed.climb} ft.`);
  if (speed.burrow) parts.push(`burrow ${speed.burrow} ft.`);
  return parts.join(', ');
}

function getAbilityModifier(score: number): string {
  const mod = Math.floor((score - 10) / 2);
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

export function BeastsReferencePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [crFilter, setCrFilter] = useState<ChallengeRating | 'all'>('all');
  const [familiarOnly, setFamiliarOnly] = useState(false);
  const [wildShapeOnly, setWildShapeOnly] = useState(false);
  const [expandedBeastId, setExpandedBeastId] = useState<string | null>(null);

  const filteredBeasts = useMemo(() => {
    let result = beasts;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (beast) =>
          beast.name.toLowerCase().includes(query) ||
          beast.type.toLowerCase().includes(query)
      );
    }

    // CR filter
    if (crFilter !== 'all') {
      result = result.filter((beast) => beast.challengeRating === crFilter);
    }

    // Familiar filter
    if (familiarOnly) {
      result = result.filter((beast) => beast.canBeFamiliar);
    }

    // Wild Shape filter
    if (wildShapeOnly) {
      result = result.filter((beast) => beast.wildShapeEligible);
    }

    return sortBeastsByCR(result);
  }, [searchQuery, crFilter, familiarOnly, wildShapeOnly]);

  const toggleBeastExpanded = (beastId: string) => {
    setExpandedBeastId((prev) => (prev === beastId ? null : beastId));
  };

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background-card/95 backdrop-blur-sm border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/create"
              className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors"
            >
              <BackIcon className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold text-text-primary">Beasts Reference</h1>
          </div>
        </div>
      </header>

      {/* Search & Filters */}
      <div className="sticky top-[65px] z-10 bg-background-primary border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
          {/* Search Input */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search beasts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-background-card border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-gold"
            />
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap gap-2">
            {/* CR Filter */}
            <select
              value={crFilter}
              onChange={(e) => setCrFilter(e.target.value as ChallengeRating | 'all')}
              className="px-3 py-2 bg-background-card border border-border-default rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-gold"
            >
              {CR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Familiar Toggle */}
            <button
              onClick={() => setFamiliarOnly(!familiarOnly)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                familiarOnly
                  ? 'bg-accent-purple text-white'
                  : 'bg-background-card border border-border-default text-text-secondary hover:text-text-primary'
              }`}
            >
              Familiars
            </button>

            {/* Wild Shape Toggle */}
            <button
              onClick={() => setWildShapeOnly(!wildShapeOnly)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                wildShapeOnly
                  ? 'bg-accent-green text-white'
                  : 'bg-background-card border border-border-default text-text-secondary hover:text-text-primary'
              }`}
            >
              Wild Shape
            </button>
          </div>
        </div>
      </div>

      {/* Info Text */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <p className="text-sm text-text-secondary">
          Beasts are used for Druid Wild Shape and the Find Familiar spell. Wild Shape CR limits:
          Level 2 (CR 1/4, no fly/swim), Level 4 (CR 1/2, no fly), Level 8 (CR 1).
        </p>
        <p className="text-sm text-text-muted mt-2">
          Showing {filteredBeasts.length} of {beasts.length} beasts
        </p>
      </div>

      {/* Beast List */}
      <main className="max-w-4xl mx-auto px-4 pb-8">
        {filteredBeasts.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-card flex items-center justify-center border border-border-default">
              <BeastIcon className="w-8 h-8 text-text-muted" />
            </div>
            <p className="text-text-secondary">No beasts match your filters.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setCrFilter('all');
                setFamiliarOnly(false);
                setWildShapeOnly(false);
              }}
              className="mt-4 px-4 py-2 text-accent-gold hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredBeasts.map((beast) => (
              <BeastCard
                key={beast.id}
                beast={beast}
                isExpanded={expandedBeastId === beast.id}
                onToggle={() => toggleBeastExpanded(beast.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

interface BeastCardProps {
  beast: Beast;
  isExpanded: boolean;
  onToggle: () => void;
}

function BeastCard({ beast, isExpanded, onToggle }: BeastCardProps) {
  return (
    <div
      id={beast.id}
      className="bg-background-card border border-border-default rounded-lg overflow-hidden"
    >
      {/* Collapsed View */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-background-elevated transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-text-primary">{beast.name}</h3>
              {beast.canBeFamiliar && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-purple/20 text-accent-purple rounded">
                  Familiar
                </span>
              )}
              {beast.wildShapeEligible && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-green/20 text-accent-green rounded">
                  Wild Shape
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-text-muted">
              {capitalizeFirst(beast.size)} {beast.type} &bull; AC {beast.armorClass} &bull; HP{' '}
              {beast.hitPoints.average}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-medium text-accent-gold">CR {beast.challengeRating}</div>
            <div className="text-xs text-text-muted">{beast.experiencePoints} XP</div>
          </div>
        </div>
        <div className="flex items-center justify-center mt-2">
          <ChevronIcon
            className={`w-5 h-5 text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Expanded View */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border-default">
          <div className="pt-4 space-y-4">
            {/* Stats Block */}
            <div className="bg-background-elevated rounded-lg p-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-text-muted">Speed:</span>{' '}
                  <span className="text-text-primary">{formatSpeed(beast.speed)}</span>
                </div>
                <div>
                  <span className="text-text-muted">Hit Points:</span>{' '}
                  <span className="text-text-primary">
                    {beast.hitPoints.average} ({beast.hitPoints.formula})
                  </span>
                </div>
              </div>
            </div>

            {/* Ability Scores */}
            <div>
              <h4 className="text-sm font-semibold text-text-primary mb-2">Ability Scores</h4>
              <div className="grid grid-cols-6 gap-2">
                {(['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const).map(
                  (ability) => (
                    <div key={ability} className="text-center">
                      <div className="text-xs text-text-muted uppercase">
                        {ability.slice(0, 3)}
                      </div>
                      <div className="text-sm font-medium text-text-primary">
                        {beast.abilityScores[ability]}
                      </div>
                      <div className="text-xs text-text-secondary">
                        ({getAbilityModifier(beast.abilityScores[ability])})
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Senses */}
            {beast.senses && beast.senses.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-1">Senses</h4>
                <p className="text-sm text-text-secondary">{beast.senses.join(', ')}</p>
              </div>
            )}

            {/* Traits */}
            {beast.traits && beast.traits.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-2">Traits</h4>
                <div className="space-y-2">
                  {beast.traits.map((trait, index) => (
                    <div key={index}>
                      <span className="text-sm font-medium text-accent-gold">{trait.name}.</span>{' '}
                      <span className="text-sm text-text-secondary">
                        <LinkedText>{trait.description}</LinkedText>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {beast.actions && beast.actions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-2">Actions</h4>
                <div className="space-y-2">
                  {beast.actions.map((action, index) => (
                    <div key={index}>
                      <span className="text-sm font-medium text-accent-red">{action.name}.</span>{' '}
                      <span className="text-sm text-text-secondary">
                        <LinkedText>{action.description}</LinkedText>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function BeastIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}
